package fr.frux.feature.treelist

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Park
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import fr.frux.core.designsystem.components.EmptyState
import fr.frux.core.designsystem.components.ErrorState
import fr.frux.core.designsystem.components.FruxTopBar
import fr.frux.core.designsystem.components.LoadingState
import fr.frux.core.designsystem.components.TreeCard
import fr.frux.core.designsystem.error.userMessage
import fr.frux.core.designsystem.icons.treeMarkerTint
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.feature.treelist.resources.Res
import fr.frux.feature.treelist.resources.treelist_empty_cta
import fr.frux.feature.treelist.resources.treelist_empty_message
import fr.frux.feature.treelist.resources.treelist_empty_title
import fr.frux.feature.treelist.resources.treelist_title
import org.jetbrains.compose.resources.stringResource
import kotlin.math.roundToInt

/**
 * Tree List Screen ("Tous les arbres").
 *
 * Displays a scrollable list of all fruit trees currently known, with Loading/Empty/Error
 * states. Reached from the Map screen's "Voir tout" action.
 *
 * @param onNavigateToTreeDetail Callback to navigate to a specific tree detail
 * @param onBack Callback when the user wants to navigate back
 * @param onAddTreeClick Optional callback for the empty state's CTA. The CTA is only shown
 * when this is provided (no "Add tree" flow is wired yet from this screen).
 */
@Composable
fun TreeListScreen(
    origin: TreeListOrigin,
    onNavigateToTreeDetail: (Long) -> Unit = {},
    onBack: () -> Unit = {},
    onAddTreeClick: (() -> Unit)? = null,
    viewModel: TreeListViewModel = rememberTreeListViewModel(origin),
) {
    val state by viewModel.state.collectAsState()

    Scaffold(
        topBar = {
            FruxTopBar(
                title = stringResource(Res.string.treelist_title),
                onBack = onBack,
            )
        },
        containerColor = MaterialTheme.colorScheme.background,
    ) { paddingValues ->
        when {
            state.isLoading -> {
                LoadingState(modifier = Modifier.padding(paddingValues))
            }

            state.error != null -> {
                ErrorState(
                    message = state.error!!.userMessage(),
                    onRetry = viewModel::loadTrees,
                    modifier = Modifier.padding(paddingValues),
                )
            }

            state.trees.isEmpty() -> {
                EmptyState(
                    icon = Icons.Outlined.Park,
                    title = stringResource(Res.string.treelist_empty_title),
                    message = stringResource(Res.string.treelist_empty_message),
                    ctaLabel = if (onAddTreeClick != null) stringResource(Res.string.treelist_empty_cta) else null,
                    onCta = onAddTreeClick,
                    modifier = Modifier.padding(paddingValues),
                )
            }

            else -> {
                LazyColumn(
                    modifier =
                        Modifier
                            .fillMaxSize()
                            .padding(paddingValues),
                    contentPadding = PaddingValues(FruxSpacing.md),
                    verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
                ) {
                    // De quoi la liste parle (#229) : le périmètre hérité de la carte, pas un
                    // inventaire global — la ligne le dit avant le premier arbre.
                    item(key = "context-header") {
                        Text(
                            text = contextLabel(state),
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                    }
                    items(state.trees, key = { it.id }) { tree ->
                        TreeCard(
                            name = tree.name,
                            meta = tree.meta,
                            status = tree.status,
                            onClick = { onNavigateToTreeDetail(tree.id) },
                            glyph = tree.glyph,
                            glyphTint = treeMarkerTint(tree.scientificName, tree.status),
                            thumbUrl = tree.photoUrl,
                            needsCheck = tree.needsCheck,
                            sightingLabel = tree.sightingLabel,
                        )
                    }
                }
            }
        }
    }
}

/** « 12 arbres à moins de 5 km autour de la vue carte » — troncature annoncée (#155/#229). */
private fun contextLabel(state: TreeListState): String {
    val count = state.trees.size
    val scope =
        state.radiusKm?.let { "à moins de ${formatRadius(it)} autour de la vue carte" }
            ?: "autour de la vue carte"
    val total = state.totalAvailable
    return if (total != null && total > count) {
        "Les $count arbres les plus proches (sur $total) $scope"
    } else {
        val trees = if (count == 1) "1 arbre" else "$count arbres"
        "$trees $scope"
    }
}

private fun formatRadius(radiusKm: Double): String =
    if (radiusKm < 1.0) {
        "${(radiusKm * 1_000).roundToInt()} m"
    } else {
        "${radiusKm.roundToInt()} km"
    }
